import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import { useAdminDestinationList } from "@/hooks/useAdminLists";
import {
  SERVICE_KIND_LABELS,
  SERVICE_BOOKING_STATUS_LABELS,
  FACET_NULL,
  type ServiceKind,
  type ServiceBookingStatus,
  type User,
  SERVICE_KINDS,
  SERVICE_BOOKING_STATUSES,
} from "@ckgo2/shared/types";
import { formatPrice } from "@ckgo2/shared/utils";
import { formatDate } from "@/lib/format";
import { enumLabel } from "@/i18n/enumLabels";
import { useAuthStore } from "@/stores/authStore";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel, { type FacetBucket } from "@/components/common/FacetPanel";
import {
  ClipboardList,
  UserPlus,
  UserMinus,
  AlertTriangle,
  Clock,
  ShieldCheck,
} from "lucide-react";

interface QueueRow {
  id: string;
  orderId?: string | null;
  kind: ServiceKind;
  status: ServiceBookingStatus;
  bookingMode?: string | null;
  snapshotName: string;
  cost: number;
  currency: string;
  confirmDeadline?: string | null;
  paymentDeadline?: string | null;
  assignedTo?: string | null;
  assignedAt?: string | null;
  destinationId?: string | null;
  supplierName?: string | null;
  orderNumber?: string | null;
  assignedToName?: string | null;
}

interface FacetedQueue {
  data: QueueRow[];
  total: number;
  page: number;
  limit: number;
  facets: Record<string, FacetBucket[]>;
  success: true;
}

interface QueueDefaults {
  kinds: string[];
  destinationIds: string[];
}

const STATUS_BADGE: Record<ServiceBookingStatus, string> = {
  draft: "bg-gray-100 text-gray-600",
  requested: "bg-blue-100 text-blue-700",
  confirmed: "bg-emerald-100 text-emerald-700",
  paid: "bg-purple-100 text-purple-700",
  cancelled: "bg-red-100 text-red-600",
};

// A booking is "urgent" when nobody owns it and its nearest deadline is within this window (or past).
const URGENT_DAYS = 7;

function nearestDeadline(r: QueueRow): string | null {
  const ds = [r.confirmDeadline, r.paymentDeadline].filter(Boolean) as string[];
  return ds.length ? ds.slice().sort()[0] : null;
}

function daysUntil(iso: string): number {
  const today = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00Z").getTime();
  const d = new Date(iso.slice(0, 10) + "T00:00:00Z").getTime();
  return Math.round((d - today) / 86400000);
}

export default function ServiceQueuePage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const qs = searchParams.toString();
  const queryClient = useQueryClient();
  const me = useAuthStore((s) => s.user);
  // Two-click take-over: holds the row id awaiting a confirm before stealing someone else's claim.
  const [takeOverId, setTakeOverId] = useState<string | null>(null);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["service-queue", qs],
    queryFn: () => api.get<FacetedQueue>(`/api/admin/service-bookings?sort=deadline&${qs}`),
  });
  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<ApiResponse<User[]>>("/api/admin/users"),
  });
  const { data: destData } = useAdminDestinationList();
  // The current user's queue landing defaults (specialist kinds + garant destinations).
  const { data: defaultsData } = useQuery({
    queryKey: ["queue-defaults"],
    queryFn: () =>
      api.get<ApiResponse<QueueDefaults>>("/api/admin/service-bookings/queue-defaults"),
    staleTime: 10 * 60 * 1000,
  });

  const userName = (id: string) => usersData?.data.find((u) => u.id === id)?.name ?? id;
  const destName = (id: string) => destData?.data.find((d) => d.id === id)?.name ?? id;

  // Default landing (E2E-018): on first load with no filters, pre-select the user's specialist kinds
  // and garant destinations so a letenkář lands on flights / a garant on their destinations. Runs
  // once per mount; clearing filters afterwards is respected (no re-apply).
  const appliedDefaults = useRef(false);
  useEffect(() => {
    if (appliedDefaults.current) return;
    const d = defaultsData?.data;
    if (!d) return;
    appliedDefaults.current = true;
    const hasViewFilter =
      searchParams.has("kind") ||
      searchParams.has("destinationId") ||
      searchParams.has("assignedTo") ||
      searchParams.has("status") ||
      searchParams.has("q");
    if (hasViewFilter) return;
    if (d.kinds.length === 0 && d.destinationIds.length === 0) return;
    const next = new URLSearchParams(searchParams);
    if (d.kinds.length) next.set("kind", d.kinds.join(","));
    if (d.destinationIds.length) next.set("destinationId", d.destinationIds.join(","));
    setSearchParams(next, { replace: true });
  }, [defaultsData, searchParams, setSearchParams]);

  const assign = useMutation({
    mutationFn: ({ id, assignedTo }: { id: string; assignedTo: string | null }) =>
      api.patch(`/api/admin/service-bookings/${id}/assign`, { assignedTo }),
    onSuccess: () => {
      setTakeOverId(null);
      queryClient.invalidateQueries({ queryKey: ["service-queue"] });
    },
  });

  const setView = (assignedTo: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (assignedTo === null) next.delete("assignedTo");
    else next.set("assignedTo", assignedTo);
    next.delete("page");
    setSearchParams(next, { replace: true });
  };
  const viewValue = searchParams.get("assignedTo");

  const rows = data?.data ?? [];
  const facets = data?.facets ?? {};
  const total = data?.total ?? 0;
  const limit = data?.limit ?? 25;
  const unassignedUrgent = rows.filter((r) => {
    if (r.assignedTo) return false;
    const nd = nearestDeadline(r);
    return nd != null && daysUntil(nd) <= URGENT_DAYS;
  }).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t("ops.serviceQueue.title")}
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">{t("ops.serviceQueue.subtitle")}</p>
      </div>

      {/* Quick views + the unassigned-urgent alarm */}
      <div className="flex flex-wrap items-center gap-2">
        <ViewChip active={viewValue === (me?.id ?? "")} onClick={() => setView(me?.id ?? null)}>
          {t("ops.serviceQueue.viewMine")}
        </ViewChip>
        <ViewChip active={viewValue === FACET_NULL} onClick={() => setView(FACET_NULL)}>
          {t("ops.serviceQueue.viewUnassigned")}
          {unassignedUrgent > 0 && (
            <span className="ml-1 rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
              {unassignedUrgent}
            </span>
          )}
        </ViewChip>
        <ViewChip active={!viewValue} onClick={() => setView(null)}>
          {t("ops.serviceQueue.viewAll")}
        </ViewChip>
        {unassignedUrgent > 0 && viewValue !== FACET_NULL && (
          <button
            onClick={() => setView(FACET_NULL)}
            className="ml-auto flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
          >
            <AlertTriangle className="h-4 w-4" />
            {t("ops.serviceQueue.unassignedUrgent", {
              count: unassignedUrgent,
              days: URGENT_DAYS,
            })}
          </button>
        )}
      </div>

      <FacetedListLayout
        searchPlaceholder={t("ops.serviceQueue.searchPlaceholder")}
        total={total}
        limit={limit}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={
          <>
            <FacetPanel
              title={t("ops.serviceQueue.facetKind")}
              paramKey="kind"
              order={{ enum: SERVICE_KINDS }}
              buckets={facets.kind ?? []}
              labelFor={(v) => enumLabel(SERVICE_KIND_LABELS, v as ServiceKind)}
            />
            <FacetPanel
              title={t("ops.serviceQueue.facetDestination")}
              paramKey="destinationId"
              order="name"
              buckets={facets.destinationId ?? []}
              labelFor={destName}
              nullLabel={t("ops.serviceQueue.noDestination")}
            />
            <FacetPanel
              title={t("ops.serviceQueue.facetStatus")}
              paramKey="status"
              order={{ enum: SERVICE_BOOKING_STATUSES }}
              buckets={facets.status ?? []}
              labelFor={(v) => enumLabel(SERVICE_BOOKING_STATUS_LABELS, v as ServiceBookingStatus)}
            />
            <FacetPanel
              title={t("ops.serviceQueue.facetSpecialist")}
              paramKey="assignedTo"
              order="name"
              buckets={facets.assignedTo ?? []}
              labelFor={userName}
              nullLabel={t("ops.serviceQueue.unassigned")}
            />
          </>
        }
      >
        <div className="card overflow-hidden p-0">
          {rows.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-gray-500">
              <ClipboardList className="mb-2 h-10 w-10 text-gray-300" />
              <p>{t("ops.serviceQueue.emptyQueue")}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  {[
                    t("ops.serviceQueue.colService"),
                    t("ops.serviceQueue.colOrder"),
                    t("ops.serviceQueue.colCost"),
                    t("ops.serviceQueue.colDeadlines"),
                    t("ops.serviceQueue.colStatus"),
                    t("ops.serviceQueue.colSpecialist"),
                    "",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map((r) => {
                  const nd = nearestDeadline(r);
                  const urgent = !r.assignedTo && nd != null && daysUntil(nd) <= URGENT_DAYS;
                  const mine = r.assignedTo && r.assignedTo === me?.id;
                  return (
                    <tr
                      key={r.id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                        urgent ? "bg-red-50/40 dark:bg-red-900/10" : ""
                      }`}
                    >
                      <td className="px-4 py-3">
                        <p className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-white">
                          {r.snapshotName}
                          {r.bookingMode === "on_demand" && (
                            <span
                              className="inline-flex items-center gap-0.5 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                              title={t("ops.serviceQueue.onDemandTitle")}
                            >
                              <ShieldCheck className="h-3 w-3" /> {t("ops.serviceQueue.onDemand")}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {enumLabel(SERVICE_KIND_LABELS, r.kind)}
                          {r.supplierName ? ` · ${r.supplierName}` : ""}
                          {r.destinationId ? ` · ${destName(r.destinationId)}` : ""}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {r.orderId ? (
                          <Link
                            to={`/orders/${r.orderId}`}
                            className="text-primary-600 dark:text-primary-400 font-mono hover:underline"
                          >
                            {r.orderNumber ?? "—"}
                          </Link>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {formatPrice(r.cost, r.currency)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {r.confirmDeadline && (
                          <div>
                            {t("ops.serviceQueue.confirmBy", {
                              date: formatDate(r.confirmDeadline),
                            })}
                          </div>
                        )}
                        {r.paymentDeadline && (
                          <div>
                            {t("ops.serviceQueue.payBy", { date: formatDate(r.paymentDeadline) })}
                          </div>
                        )}
                        {!r.confirmDeadline && !r.paymentDeadline && "—"}
                        {urgent && nd && (
                          <div className="mt-0.5 inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
                            <Clock className="h-3 w-3" />
                            {daysUntil(nd) < 0
                              ? t("ops.serviceQueue.overdueDays", { days: -daysUntil(nd) })
                              : daysUntil(nd) === 0
                                ? t("ops.serviceQueue.dueToday")
                                : t("ops.serviceQueue.dueInDays", { days: daysUntil(nd) })}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[r.status]}`}
                        >
                          {enumLabel(SERVICE_BOOKING_STATUS_LABELS, r.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {r.assignedToName ? (
                          <span
                            className={`inline-flex flex-col rounded-md px-2 py-1 ${
                              mine
                                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
                                : "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
                            }`}
                          >
                            <span className="font-medium">
                              {mine ? t("ops.serviceQueue.you") : r.assignedToName}
                            </span>
                            {r.assignedAt && (
                              <span className="text-[10px] opacity-70">
                                {t("ops.serviceQueue.since", { date: formatDate(r.assignedAt) })}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400">{t("ops.serviceQueue.unassigned")}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {mine ? (
                          <button
                            onClick={() => assign.mutate({ id: r.id, assignedTo: null })}
                            className="btn-ghost btn-sm"
                            title={t("ops.serviceQueue.release")}
                          >
                            <UserMinus className="h-4 w-4" />
                            {t("ops.serviceQueue.release")}
                          </button>
                        ) : r.assignedTo ? (
                          takeOverId === r.id ? (
                            <button
                              onClick={() =>
                                assign.mutate({ id: r.id, assignedTo: me?.id ?? null })
                              }
                              className="btn-sm rounded-md bg-amber-500 px-2 py-1 text-white hover:bg-amber-600"
                              title={t("ops.serviceQueue.takeOverFrom", {
                                name: r.assignedToName,
                              })}
                            >
                              {t("ops.serviceQueue.confirmTakeOver")}
                            </button>
                          ) : (
                            <button
                              onClick={() => setTakeOverId(r.id)}
                              className="btn-ghost btn-sm text-amber-600"
                              title={t("ops.serviceQueue.claimedBy", { name: r.assignedToName })}
                            >
                              <UserPlus className="h-4 w-4" />
                              {t("ops.serviceQueue.takeOver")}
                            </button>
                          )
                        ) : (
                          <button
                            onClick={() => assign.mutate({ id: r.id, assignedTo: me?.id ?? null })}
                            className="btn-ghost btn-sm"
                            title={t("ops.serviceQueue.claim")}
                          >
                            <UserPlus className="h-4 w-4" />
                            {t("ops.serviceQueue.claim")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}

function ViewChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
          : "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50"
      }`}
    >
      {children}
    </button>
  );
}
